import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Inbox, ArrowUpRight } from 'lucide-react';
import { api } from '../../lib/api';
import EmptyState from '../../components/crm/EmptyState';
import FilterChip from '../../components/crm/FilterChip';
import EmailComposer from '../../components/admin/EmailComposer';
import ConversationThread, { Avatar } from '../../components/admin/ConversationThread';
import { ResponsibleMenu } from './AccountDetail/CommunicationsTab';
import {
  shortName, avatarTone, firstName, localPart, contactRoleLabel, nexsavAddressFor,
  latestHumanInbound, waitingFor, announceNeedsReplyChange,
} from '../../lib/communications';

// /admin/communications?tab=needs-reply — schools that wrote back (a real
// person, after the cutoff) and haven't heard from us since. Oldest-waiting
// first. Replying or marking resolved clears the row and selects the next.

export default function NeedsReplyTab({ user, onToast, onCounts, counts }) {
  const [scope, setScope] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(null); // 'resolve' | 'responsible'

  const refreshCounts = useCallback(async () => {
    try {
      const d = await api.get('/api/admin/communications/needs-reply?count=1');
      onCounts?.({ total: Number(d?.total) || 0, mine: Number(d?.mine) || 0 });
    } catch { /* badge stays as it was */ }
  }, [onCounts]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [res, boxes] = await Promise.all([
        api.get(`/api/admin/communications/needs-reply?scope=${scope}&limit=50`),
        api.get('/api/admin/communications/mailboxes').catch(() => null),
      ]);
      const list = res?.conversations || [];
      setRows(list);
      setStaff((boxes?.staff || []).map((s) => ({ id: s.id, name: s.name, email: s.email, role: s.role })));
      setSelectedId((cur) => (cur && list.some((c) => c.id === cur) ? cur : (list[0]?.id || null)));
      onCounts?.((prev) => ({ ...(prev || {}), [scope === 'mine' ? 'mine' : 'total']: Number(res?.total) || list.length }));
    } catch (err) {
      setError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [scope, onCounts]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  const selected = useMemo(() => rows.find((c) => c.id === selectedId) || null, [rows, selectedId]);

  // Drop a row (after reply/resolve) and move the selection to its neighbour.
  const removeRow = (id) => {
    setRows((rs) => {
      const idx = rs.findIndex((c) => c.id === id);
      const next = rs.filter((c) => c.id !== id);
      setSelectedId((cur) => (cur === id ? (next[Math.min(idx, next.length - 1)]?.id || null) : cur));
      return next;
    });
    announceNeedsReplyChange();
    refreshCounts();
  };

  const resolve = async (c) => {
    setBusy('resolve');
    try {
      await api.post(`/api/admin/communications/conversations/${c.id}/resolve`, {});
      onToast?.({ message: 'Marked resolved', detail: `${c.accountName || 'The school'} no longer counts as waiting.` });
      removeRow(c.id);
    } catch (err) {
      onToast?.({ message: 'Couldn’t mark resolved', detail: err?.message });
    } finally {
      setBusy(null);
    }
  };

  const reassign = async (c, userId) => {
    setBusy('responsible');
    try {
      const res = await api.put(`/api/admin/communications/conversations/${c.id}/responsible`, { user_id: userId || null });
      const resp = res?.responsible || null;
      setRows((rs) => rs.map((r) => (r.id === c.id ? { ...r, responsibleUserId: resp?.id || null, responsibleName: resp?.name || null } : r)));
      onToast?.({ message: resp ? `${firstName(resp.name)} is now responsible` : 'Responsible cleared' });
      refreshCounts();
    } catch (err) {
      onToast?.({ message: 'Couldn’t reassign', detail: err?.message });
    } finally {
      setBusy(null);
    }
  };

  const total = counts?.total ?? null;
  const mine = counts?.mine ?? null;

  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <p className="text-[13px] text-gray-400 max-w-xl">
          Schools that wrote back and haven’t heard from us. Out-of-office notices and “thanks!” don’t land here.
          {user?.role === 'ops' && <span className="block text-[12px] text-gray-500 mt-1" data-testid="scope-note">You see the conversations your own mailbox took part in, plus any you’re responsible for. Admins see everyone’s.</span>}
        </p>
        <div className="flex items-center gap-1.5" role="group" aria-label="Needs reply scope">
          <FilterChip active={scope === 'all'} onClick={() => setScope('all')} aria-pressed={scope === 'all'} data-testid="needs-reply-scope-all">
            Everyone <span className="opacity-60 tabular-nums">{total ?? '…'}</span>
          </FilterChip>
          <FilterChip active={scope === 'mine'} onClick={() => setScope('mine')} aria-pressed={scope === 'mine'} data-testid="needs-reply-scope-mine">
            Mine <span className="opacity-60 tabular-nums">{mine ?? '…'}</span>
          </FilterChip>
        </div>
      </div>

      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">{error}</div>}

      {loading && rows.length === 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,380px)_minmax(0,1fr)] gap-3 animate-pulse">
          <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-xl bg-white/[0.03]" />)}</div>
          <div className="h-72 rounded-xl bg-white/[0.03]" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title={scope === 'mine' ? 'Nothing assigned to you is waiting.' : 'Nothing waiting — every school that wrote has heard back.'}
          description={scope === 'mine' ? 'Switch to Everyone to see the whole queue.' : 'Replies sent from Gmail directly clear rows here too.'}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,380px)_minmax(0,1fr)] gap-3 items-start">
          <ul className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden list-none m-0 p-0 divide-y divide-white/[0.05]" aria-label="Conversations waiting for a reply">
            {rows.map((c) => (
              <li key={c.id}>
                <QueueRow conversation={c} selected={c.id === selectedId} onSelect={() => setSelectedId(c.id)} />
              </li>
            ))}
          </ul>

          {selected && (
            <ConversationPane
              key={selected.id}
              conversation={selected}
              staff={staff}
              busy={busy}
              onResolve={() => resolve(selected)}
              onReassign={(uid) => reassign(selected, uid)}
              onSent={() => removeRow(selected.id)}
              onToast={onToast}
            />
          )}
        </div>
      )}

      <p className="mt-4 text-[11.5px] text-gray-600 leading-relaxed">
        <b className="text-gray-500">How something gets here:</b> a real person at a school sent the latest message on a thread and no one at NexSav has replied or marked it resolved. Replying, or a reply sent from Gmail directly, clears it automatically. Red age = older than 3 days.
      </p>
    </div>
  );
}

function fromLine(m) {
  if (!m) return '';
  const p = (m.participants || []).find((x) => x.kind === 'from');
  const who = shortName(m.from?.name || p?.contactName, m.from?.email);
  const role = p?.contactRoleTitle || (p?.contactRole ? contactRoleLabel(p.contactRole) : '');
  const box = localPart(nexsavAddressFor(m));
  return [[who, role].filter(Boolean).join(' · '), box].filter(Boolean).join(' → ');
}

function QueueRow({ conversation: c, selected, onSelect }) {
  const last = latestHumanInbound(c);
  const wait = waitingFor(c.waitingSince || c.lastHumanInboundAt);
  const snippet = last?.snippet || (last?.bodyText || '').slice(0, 200) || '';
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      data-testid="needs-reply-row"
      className={`w-full text-left grid grid-cols-[28px_minmax(0,1fr)_auto] gap-3 items-start px-3.5 py-3 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#B0FC38] focus-visible:-outline-offset-2 ${
        selected ? 'bg-[#B0FC38]/[0.06] border-l-2 border-l-[#B0FC38] pl-3' : 'hover:bg-white/[0.03] border-l-2 border-l-transparent pl-3'
      }`}
    >
      <Avatar label={last?.from?.name || last?.from?.email || c.subject} className={avatarTone(last?.from?.email || last?.from?.name)} />
      <span className="min-w-0 flex flex-col">
        <span className="text-[13px] font-semibold text-white truncate">{c.accountName || c.subject || '(no school)'}</span>
        <span className="text-[11.5px] text-gray-500 truncate mt-0.5">{fromLine(last)}</span>
        {snippet && <span className="text-[12px] text-gray-400 mt-1 line-clamp-2">{snippet}</span>}
      </span>
      <span className="flex flex-col items-end gap-1.5 shrink-0">
        <span className={`text-[11px] tabular-nums whitespace-nowrap ${wait.overdue ? 'text-red-300' : 'text-amber-300'}`} data-testid="needs-reply-age">{wait.label}</span>
        {c.responsibleName && <Avatar label={c.responsibleName} className={avatarTone(c.responsibleName)} size="sm" />}
      </span>
    </button>
  );
}

function ConversationPane({ conversation: c, staff, busy, onResolve, onReassign, onSent, onToast }) {
  return (
    <section className="rounded-xl border border-white/[0.06] bg-white/[0.02] flex flex-col min-w-0" aria-label={`Conversation with ${c.accountName || 'school'}`} data-testid="needs-reply-pane">
      <header className="px-4 py-3 border-b border-white/[0.06] flex items-start justify-between gap-3 flex-wrap">
        <h3 className="text-[13.5px] font-semibold text-white min-w-0 truncate">
          {c.accountName ? `${c.accountName} · ` : ''}{c.subject || '(no subject)'}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <ResponsibleMenu value={c.responsibleUserId} name={c.responsibleName} staff={staff} busy={busy === 'responsible'} onChange={onReassign} />
          {c.accountId && (
            <Link
              to={`/admin/accounts/${c.accountId}?tab=communications`}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[12px] text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors"
            >
              Open account <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
            </Link>
          )}
        </div>
      </header>
      <div className="px-4 py-3 max-h-[52vh] overflow-y-auto">
        <ConversationThread messages={c.messages || []} />
      </div>
      <div className="px-3 pb-3">
        <EmailComposer
          mode="reply"
          variant="inline"
          conversation={c}
          accountId={c.accountId}
          accountName={c.accountName}
          onSent={onSent}
          onToast={onToast}
          onResolve={onResolve}
          resolving={busy === 'resolve'}
        />
      </div>
    </section>
  );
}
